import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';
import { LuCloudUpload, LuFile, LuFileImage, LuFileText, LuLoaderCircle, LuTrash2 } from 'react-icons/lu';
import IconButton from '../../../components/ui/IconButton';
import { storage } from '../../../config/firebaseConfig';
import {
  SHIPMENT_ALLOWED_FILE_TYPES,
  SHIPMENT_MAX_FILE_SIZE,
  formatFileSize,
} from '../../../utils/shipments';
import useCargoText from './useCargoText';

const fileIconFor = (type = '') => {
  if (type.startsWith('image/')) return LuFileImage;
  if (type === 'application/pdf') return LuFileText;
  return LuFile;
};

/**
 * Kargo fişi / irsaliye / teslim tutanağı yükleme alanı. Dosyalar doğrudan
 * Storage'a yüklenir; `onUploadingChange` ile form kaydı yükleme bitene kadar
 * bekletilir.
 */
const CargoAttachmentsField = ({ attachments, onChange, onUploadingChange }) => {
  const { label } = useCargoText();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const setBusy = (value) => {
    setUploading(value);
    onUploadingChange(value);
  };

  const uploadFiles = async (fileList) => {
    const accepted = Array.from(fileList || []).filter((file) => {
      if (!SHIPMENT_ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(label('toast.unsupported', { name: file.name }));
        return false;
      }
      if (file.size > SHIPMENT_MAX_FILE_SIZE) {
        toast.error(label('toast.tooLarge', { name: file.name }));
        return false;
      }
      return true;
    });
    if (accepted.length === 0) return;

    setBusy(true);
    try {
      const uploaded = await Promise.all(
        accepted.map(async (file) => {
          const storagePath = `companyShipments/${Date.now()}_${file.name}`;
          const snapshot = await uploadBytes(ref(storage, storagePath), file);
          return {
            name: file.name,
            url: await getDownloadURL(snapshot.ref),
            type: file.type,
            size: file.size,
            storagePath,
            uploadedAt: new Date().toISOString(),
          };
        })
      );
      onChange([...attachments, ...uploaded]);
    } catch (error) {
      console.error('Kargo dosyası yüklenemedi:', error);
      toast.error(label('toast.uploadError'));
    } finally {
      setBusy(false);
    }
  };

  const removeAttachment = async (index) => {
    const attachment = attachments[index];
    try {
      if (attachment?.storagePath) await deleteObject(ref(storage, attachment.storagePath));
    } catch (error) {
      // Storage'dan silinemese bile kayıttan düşürülür
      console.error('Kargo dosyası silinemedi:', error);
    }
    onChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <div className="tw-space-y-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          uploadFiles(e.dataTransfer.files);
        }}
        className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-6 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
          dragActive
            ? 'tw-border-teal-500 tw-bg-teal-500/[0.07]'
            : 'tw-border-slate-300 tw-bg-white/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02]'
        }`}
      >
        {uploading
          ? <LuLoaderCircle aria-hidden="true" className="tw-h-8 tw-w-8 tw-animate-spin tw-text-teal-500" />
          : <LuCloudUpload aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-teal-500" />}
        <span className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
          {uploading ? label('files.uploading') : label('files.drop')}
        </span>
        <span className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{label('files.dropHint')}</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={SHIPMENT_ALLOWED_FILE_TYPES.join(',')}
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {attachments.length > 0 && (
        <ul className="tw-space-y-2">
          {attachments.map((file, index) => {
            const Icon = fileIconFor(file.type);
            return (
              <li key={file.storagePath || `${file.name}-${index}`} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/80 tw-py-1.5 tw-pl-3 tw-pr-1.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <Icon aria-hidden="true" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-slate-400" />
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-sky-700 hover:tw-underline dark:tw-text-sky-300"
                >
                  {file.name}
                </a>
                <span className="tw-shrink-0 tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{formatFileSize(file.size)}</span>
                <IconButton label={label('files.remove')} tone="alert" onClick={() => removeAttachment(index)}>
                  <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                </IconButton>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

CargoAttachmentsField.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  onUploadingChange: PropTypes.func.isRequired,
};

export default CargoAttachmentsField;
